import { Outlet, createFileRoute, redirect } from "@tanstack/react-router";
import { websiteConfigQueryOptions } from "@/lib/website-config";
import { SalesChannels } from "@/lib/storefront/channel";
import Cart from "@/components/cart/cart";
import { useMediaQuery } from "@/hooks/use-media-query";
import { useCartStore } from "@/components/cart/store";
import { formatNumberToCurrency } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import Decimal from "decimal.js";
import { OrderTypes } from "@/lib/supabase/enums";
import { useMenuQuery } from "@/hooks/use-menu-query";
import type { Product } from "@/lib/products";
import { useEnsureValidOrderType } from "@/hooks/use-has-delivery-shifts";

export const Route = createFileRoute("/$locale/menu")({
  // A webshop has no restaurant menu; its catalogue is on the home page.
  beforeLoad: async ({ context, params }) => {
    const websiteConfig = await context.queryClient.ensureQueryData(
      websiteConfigQueryOptions()
    );
    if (websiteConfig.channel === SalesChannels.storefront) {
      throw redirect({
        to: "/$locale",
        params: { locale: params.locale },
        search: {},
      });
    }
  },
  component: RouteComponent,
});

// Helper function to calculate total option price for an item
const getSelectedOptionsTotalPrice = (
  product: Product | undefined,
  selectedOptions: Record<string, string> | undefined
): number => {
  if (!product || !selectedOptions) return 0;

  let total = 0;

  product.productOptionSubgroups?.forEach((subgroup) => {
    const selectedOptionValue = selectedOptions[subgroup.id];
    if (selectedOptionValue) {
      // Handle comma-separated option IDs (for optional checkboxes)
      const optionIds = selectedOptionValue.split(",");
      optionIds.forEach((optionId) => {
        const option = subgroup.productOptions?.find(
          (opt) => opt.id === optionId.trim()
        );
        if (option) {
          total += option.price;
        }
      });
    }
  });

  return total;
};

function RouteComponent() {
  //global state
  const cart = useCartStore((state) => state.cart);

  //translations
  const { t } = useTranslation("translation");

  //hooks
  const { isSize } = useMediaQuery(1024);

  // Fall back to pickup (take away) while browsing when the business has no
  // delivery shifts, so the cart never sits on an unavailable delivery type.
  useEnsureValidOrderType();

  //queries
  const { data: productsByCategory } = useMenuQuery();

  //computed
  const isCartEmpty = cart.items.length === 0;

  const itemsTotal = cart.items.reduce((acc, item) => {
    const product = productsByCategory
      ?.flatMap((category) => category.products)
      .find((p) => p.id === item.productId);

    const basePrice = item.price;
    const optionsPrice = getSelectedOptionsTotalPrice(
      product,
      item.selectedOptions
    );
    const itemTotal = (basePrice + optionsPrice) * item.quantity;

    return acc + itemTotal;
  }, 0);

  const deliveryFee =
    cart.orderType === OrderTypes.pickup ? 0 : cart.fees.deliveryFee;

  // Calculate coupon discount
  let couponDiscount = 0;
  if (cart.coupon?.coupon && cart.coupon.coupon.type === "cart_adjustment") {
    const subtotalBeforeDiscount = itemsTotal + deliveryFee;
    if (cart.coupon.coupon.discountType === "percentage") {
      couponDiscount = new Decimal(subtotalBeforeDiscount)
        .mul(cart.coupon.coupon.discountValue)
        .div(100)
        .toNumber();
    } else if (cart.coupon.coupon.discountType === "fixed") {
      couponDiscount = cart.coupon.coupon.discountValue / 100;
    }
  }

  const cartTotal = itemsTotal + deliveryFee - couponDiscount;

  return (
    <div
      className="flex lg:flex-row flex-col relative lg:items-start flex-1"
      data-scroll-container
    >
      <div className="flex-1">
        <Outlet />
      </div>
      {!isSize && (
        <div className="flex w-86 bg-white shadow sticky top-0 self-start max-h-screen h-full">
          <Cart className="flex-1" />
        </div>
      )}
      {isSize && !isCartEmpty && (
        <div className="sticky bottom-0 p-4 flex items-center rounded-t-xl bg-[#fdfdfb] drop-shadow">
          <Cart>
            <button className="cursor-pointer flex items-center justify-center w-full p-2 rounded-full bg-black/90 hover:bg-black text-white text-lg font-bold transition-colors">
              {t("Cart.view_cart", {
                cart_total: formatNumberToCurrency({ number: cartTotal }),
              })}
            </button>
          </Cart>
        </div>
      )}
    </div>
  );
}
